<template>
  <div class="TagConfigPanel DetailGrid">
    <div class="DetailGrid__nav">
      <h2 class="h3 TagConfigPanel__header">
        <span>
          {{ $t('ExportConfigPanel.exportManagement') }}
        </span>
      </h2>
      <div class="mt-3">
        <h4 class="mb-3">
          {{ $t('navbar.processes') }}
        </h4>
        <div class="d-flex flex-column">
          <DataTable
            :key="tableKey"
            :value="processTypes"
            :selection.sync="selectedRows"
            data-key="field"
            class="processes"
          >
            <Column
              v-for="(column, index) in columns"
              :key="column.field"
            >
              <template #header>
                <span v-if="exportTotal">
                  <CheckBox
                    v-if="!index"
                    v-model="allSelected"
                    :binary="true"
                    class="mr-3"
                    @input="handleSelectAll"
                  />
                  {{ column.header }}
                </span>
                <Skeleton v-else />
              </template>
              <template #body="{ data }">
                <span v-if="exportTotal">
                  <CheckBox
                    v-if="!index && data.value !== 'total'"
                    v-model="selectedRows"
                    :input-id="data.value"
                    :value="data.value"
                    class="mr-3"
                  />
                  {{ data[column.field] }}
                </span>
                <Skeleton v-else />
              </template>
            </Column>
          </DataTable>
          <div class="d-flex align-items-center mt-3">
            <div>
              <Button
                type="button"
                icon="pi pi-external-link"
                :loading="isExporting"
                :disabled="isExporting || !selectedRows.length"
                :label="$t('ExportConfigPanel.exportSelectedProcesses')"
                class="p-button p-button-success"
                @click="exportAllRecords()"
              />
            </div>
            <div
              v-if="isExporting"
              class="ml-4"
            >
              {{ totalRecords.count }} / {{ exportTotal }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { exportCsv } from '@/config'
import { downloadExcel } from '@/services/exportCsv'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import CheckBox from 'primevue/checkbox'
import Skeleton from 'primevue/skeleton'

export default {
  name: 'ExportConfigPanel',
  components: {
    Button,
    DataTable,
    Column,
    Skeleton,
    CheckBox
  },

  data() {
    return {
      isExporting: false,
      totalRecords: { count: 0 },
      defaultTableFields: [],
      exportPageByType: {},
      exportTotal: 0,
      processCounts: null,
      processTypes: [],
      tableData: [],
      tableKey: 0,
      selectedRows: [],
      allSelected: true
    }
  },

  computed: {
    ...mapGetters('tenant', ['getDefaultProcessTableFields', 'getActiveProcessTypes', 'getProcessFieldLabels']),
    ...mapGetters('processes', ['getCurrentProcessType', 'getCounts']),

    isArrayDefaultFields () {
      return this.getDefaultProcessTableFields && Array.isArray(this.getDefaultProcessTableFields)
    },
    defaultFieldsNoProcessType () {
      return this.isArrayDefaultFields ? this.getDefaultProcessTableFields : []
    },
    columns () {
      return [
        { field: 'label', header: this.$i18n.t('recordList.type') },
        { field: 'exportCount', header: this.$i18n.t('Default.loadedForExport') },
        { field: 'count', header: this.$i18n.t('RealisationProcessStatus.numberOfProcesses') }
      ]
    }
  },

  watch: {
    selectedRows: {
      handler (rows) {
        this.allSelected = rows.length === Object.keys(this.exportPageByType).length
      }
    }
  },

  async created () {
    this.processTypes = [
      ...this.getActiveProcessTypes,
      { label: this.$i18n.t('KeyMetrics.totals'), count: this.exportTotal, value: 'total' }
    ]
    const counts = await this.fetchProcessesForExport ({ processType: 'all', countsOnly: true })

    this.processTypes = this.getActiveProcessTypes
      .filter(type => Object.keys(counts).includes(type.value))
      .map(p => {
        p.count = counts[p.value]
        p.exportCount = 0
        p.label = this.$i18n.t(`processes.sidebar.process_types.${p.value}`)

        this.exportPageByType[p.value] = 1
        return p
      })
      .sort((a, b) => a.value.localeCompare(b.value))

    this.processTypes.push({ label: this.$i18n.t('KeyMetrics.totals'), count: this.exportTotal, value: 'total' })
    this.selectedRows = Object.keys(this.exportPageByType)
    this.tableKey++
  },

  methods: {
    handleSelectAll (event) {
      this.selectedRows = event ? Object.keys(this.exportPageByType) : []
    },

    async exportAllRecords () {
      this.tableData = []
      this.tableKey ++

      this.processTypes.map(type => {
        type.exportCount = 0
        return type
      })

      await Promise.all(
        this.selectedRows
          .filter(type => type.value !== 'total')
          .map(async (type) => await this.exportCsv(type))
      )

      this.tableData.sort((a, b) => a.tab.localeCompare(b.tab))

      exportCsv.file.name = 'processes'
      exportCsv.file.acronym = this.$i18n.t('ExportConfigPanel.allProcesses').toUpperCase()
      await downloadExcel({ config: exportCsv, records: this.tableData, tabs: true })

      this.isExporting = false
      this.totalRecords.count = 0
      this.tableData = []
    },

     async fetchProcessesForExport ({ processType, countsOnly = false, allProcessesByType = [] }) {
      this.isExporting = !countsOnly
      const perPage = countsOnly ? 1 : 400
      // &isExport=true
      const query = `processType=${processType}&page=${this.exportPageByType[processType] || 1}&pageSize=${perPage}${countsOnly ? '&countsOnly=true' : ''}`
      const api = await fetch(`${process.env.VUE_APP_AWS_API_BASE_URL}/processes?${query}`, {
        method: 'GET',
        headers: {
          'x-api-key': process.env.VUE_APP_AWS_API_KEY,
        }
      })
      if (! api.ok) {
        throw new Error('server error')
      }

      const response = await api.json()
      // Get counts (metaSearch only) for all processes //
      if (countsOnly) {
        this.exportTotal = response.data.meta.total
        this.processCounts = response.data.meta
        return response.data.meta.facets?.processType
      }

      allProcessesByType = [...allProcessesByType, ...response.data.records]
      const total = response.data.meta.total // Process type total
      const currentProcessType = this.processTypes.find(type => type.value === processType)
      const loadedTotal = this.processTypes.find(type => type.value === 'total')
      loadedTotal.exportCount += response.data.records.length

      currentProcessType.exportCount += response.data.records.length
      this.totalRecords.count += response.data.records.length

      this.tableKey++

      const exportPagesLength = total / perPage
      if (exportPagesLength > this.exportPageByType[processType]) {
        this.exportPageByType[processType]++

        return await this.fetchProcessesForExport({ processType, allProcessesByType })
      }

      return allProcessesByType
    },

    async exportCsv (type) {
      const processes = await this.fetchProcessesForExport({ after: null, processType: type })
        .catch(err => console.log(err))

      if (!processes) return

      this.exportPageByType[type] = 1

      exportCsv.worksheetColumns = []
      // TEMP: Add these two fields on the fly //
      const processType = {
        field: 'processType',
        path: 'processType.value',
        width: 200,
        label: 'Type proces'
      }

      const fields = this.getDefaultProcessTableFields[type] || this.getDefaultProcessTableFields.realisation
      if (!fields.some(field => field.field === 'processType')) {
        JSON.parse(JSON.stringify(fields)).splice(2, 0, processType)

      }

      fields
        .filter(column => !column.hidden)
        .map(column => {
          exportCsv.worksheetColumns.push({
            header: this.getProcessFieldLabels?.[column.field]?.[this.$i18n.locale] || this.setTranslation(`recordList.${column.field}`) || column.label,
            key: column.field,
            link: column.link || column.fn === 'setLink',
            fn: column.fn,
            width: Math.ceil(column.width / 10), // approx. translation to Excel column width
          })
        })

      const records = JSON.parse(JSON.stringify(processes))
      const data = {
        type,
        tab: `${this.$i18n.t(`processTypes.${type}`)} (${records.length})`,
        data: records,
        columns: exportCsv.worksheetColumns //fields
      }
      this.tableData.push(data)

      return records
    },

    setTranslation (translation) {
      return this.$i18n.te(translation) ? this.$i18n.t(translation) : null
    }
  },
}
</script>

<style lang="scss" scoped>
.TagConfigPanel {

  &__header {
    display: flex;
    justify-content: space-between;
  }
}
@keyframes p-progress-spinner-color {
    0% {
        stroke: #fff;
    }
    100% {
        stroke: #fff;
    }
}

.processes {
  max-width: 900px;
}

::v-deep {
  tbody tr:last-child {
    font-weight: bold;
    background: #f8f9fa !important;
  }
  tbody tr td:not(:first-child),
  thead tr th:not(:first-child) {
    text-align: right !important;
    div {
      justify-content: flex-end !important;
    }
  }

  .p-datatable .p-datatable-tbody tr.p-highlight:not(:last-child) {
    background: none !important;
  }
}


</style>
